import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Lock, 
  FileCheck, 
  AlertTriangle, 
  CheckCircle, 
  ArrowRight, 
  Zap,
  Server,
  Globe,
  FileText,
  ChevronRight,
  Menu,
  X,
  Bot,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  Eye,
  Ban,
  Brain,
  Search,
  AlertCircle,
  Activity,
  UserX,
  Database
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Feature Card Component with Hover Expansion
const FeatureCard = ({ feature, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      viewport={{ once: true }}
      className="bg-card border border-border rounded-sm p-8 card-hover group relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
        <feature.icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="text-xl font-bold font-[Chivo] mb-3">{feature.title}</h3>
      <p className="text-muted-foreground">{feature.description}</p>
      
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-4 border-t border-border/50">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.expandedContent}
              </p>
              {feature.bulletPoints && (
                <ul className="mt-3 space-y-2">
                  {feature.bulletPoints.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const complianceFrameworks = [
    { name: "HIPAA", description: "Health Insurance Portability and Accountability Act" },
    { name: "SOC 2", description: "Service Organization Control 2" },
    { name: "NIST", description: "National Institute of Standards and Technology" },
    { name: "GDPR", description: "General Data Protection Regulation" },
    { name: "CCPA", description: "California Consumer Privacy Act" },
    { name: "EU AI Act", description: "European Union AI Regulation" }
  ];

  const penTestFeatures = [
    {
      icon: Globe,
      title: "Web Application Scanning",
      description: "Comprehensive vulnerability assessment for web applications handling PHI",
      expandedContent: "Our web scanner tests patient portals, EHR systems, and healthcare APIs for OWASP Top 10 vulnerabilities including SQL injection, XSS, and authentication flaws that could expose protected health information.",
      bulletPoints: [
        "OWASP Top 10 vulnerability detection",
        "API security testing for FHIR/HL7 endpoints",
        "Session management and authentication testing",
        "PHI exposure risk identification"
      ]
    },
    {
      icon: Server,
      title: "Network Infrastructure",
      description: "Deep analysis of network security configurations and vulnerabilities",
      expandedContent: "Evaluate your network architecture for security gaps that could allow unauthorized access to PHI. We assess firewalls, VPNs, network segmentation, and identify exposed services that increase your attack surface.",
      bulletPoints: [
        "Port scanning and service enumeration",
        "Firewall rule assessment",
        "Network segmentation verification",
        "Encryption protocol analysis (TLS/SSL)"
      ]
    },
    {
      icon: FileCheck,
      title: "Compliance Auditing",
      description: "Automated compliance checks against HIPAA, SOC 2, NIST, and more",
      expandedContent: "Map your security controls against major healthcare compliance frameworks. Our automated auditing generates evidence documentation and identifies gaps before your next audit, saving hundreds of hours in preparation.",
      bulletPoints: [
        "HIPAA Security Rule mapping",
        "SOC 2 Type II control verification",
        "NIST Cybersecurity Framework alignment",
        "Audit-ready evidence documentation"
      ]
    },
    {
      icon: Zap,
      title: "24/7 Monitoring & Alerts",
      description: "Automated scheduled scans with email alerts for critical vulnerabilities",
      expandedContent: "Set up continuous security monitoring with customizable scan schedules. Receive instant email alerts when critical or high-severity vulnerabilities are detected, allowing your team to respond before attackers exploit weaknesses.",
      bulletPoints: [
        "Configurable scan frequency (6h/12h/24h/weekly)",
        "Instant email alerts for Critical & High findings",
        "Alert dashboard with severity tracking",
        "Trend analysis for security posture over time"
      ]
    }
  ];

  // AI Tools that are monitored
  const aiTools = [
    "ChatGPT", "Claude", "Gemini", "GitHub Copilot", "Microsoft Copilot",
    "Perplexity", "Notion AI", "Grammarly", "Jasper AI", "Midjourney"
  ];

  // Sensitive data types detected
  const sensitiveDataTypes = [
    { type: "SSN", label: "Social Security Numbers", risk: "critical" },
    { type: "PHI", label: "Medical Records & Diagnoses", risk: "critical" },
    { type: "PII", label: "Names, DOB, Addresses", risk: "high" },
    { type: "Financial", label: "Credit Cards & Bank Info", risk: "critical" },
    { type: "Credentials", label: "API Keys & Passwords", risk: "critical" },
    { type: "Contact", label: "Emails & Phone Numbers", risk: "medium" }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Grid pattern */}
      <div className="fixed inset-0 grid-pattern pointer-events-none" />
      
      {/* Navigation */}
      <nav className="relative z-50 border-b border-border/50 backdrop-blur-sm bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold font-[Chivo]">AI Governance Office</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#ai-governance" className="text-muted-foreground hover:text-foreground transition-colors">AI Governance</a>
              <a href="#threat-defense" className="text-muted-foreground hover:text-foreground transition-colors">Threat Defense</a>
              <a href="#pen-testing" className="text-muted-foreground hover:text-foreground transition-colors">Pen Testing</a>
              <a href="#compliance" className="text-muted-foreground hover:text-foreground transition-colors">Compliance</a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
              <button onClick={() => navigate('/risk-assessment')} className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium">Free Risk Assessment</button>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
                Sign In
              </Button>
              <Button onClick={() => navigate('/register')} className="glow-primary" data-testid="nav-register-btn">
                Start Free Trial
              </Button>
            </div>

            <button 
              className="md:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden absolute top-16 left-0 right-0 bg-card border-b border-border p-4 space-y-4"
          >
            <a href="#ai-governance" className="block text-muted-foreground hover:text-foreground">AI Governance</a>
            <a href="#threat-defense" className="block text-muted-foreground hover:text-foreground">Threat Defense</a>
            <a href="#pen-testing" className="block text-muted-foreground hover:text-foreground">Pen Testing</a>
            <a href="#compliance" className="block text-muted-foreground hover:text-foreground">Compliance</a>
            <a href="#pricing" className="block text-muted-foreground hover:text-foreground">Pricing</a>
            <button onClick={() => navigate('/risk-assessment')} className="block text-yellow-400 font-medium">Free Risk Assessment</button>
            <div className="pt-4 space-y-2 border-t border-border">
              <Button variant="ghost" className="w-full" onClick={() => navigate('/login')}>Sign In</Button>
              <Button className="w-full glow-primary" onClick={() => navigate('/register')}>Start Free Trial</Button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section - AI Governance Lead */}
      <section className="relative pt-24 pb-2">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-cyan-500/5" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-7xl md:text-9xl font-black tracking-tight font-[Chivo]">
                <span className="text-yellow-400">AI Governance</span>
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Governance Section - Primary Feature */}
      <section id="ai-governance" className="pt-4 pb-24 bg-gradient-to-b from-cyan-950/10 via-background to-background border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-bold font-[Chivo] mb-6">
                Monitor. Detect. Protect. <span className="text-red-500">Prevent.</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
                Your employees are using AI tools every day. Without oversight, sensitive patient data 
                can leak to non-HIPAA-compliant services. We give you complete visibility and control.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold"
                  onClick={() => navigate('/risk-assessment')}
                >
                  Take Free Risk Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/register')}
                >
                  Start Free Trial
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Stats Row */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
          >
            {[
              { value: "20+", label: "AI Services Tracked", subtext: "ChatGPT, Claude, Copilot..." },
              { value: "10+", label: "Data Types Detected", subtext: "SSN, PHI, PII, API Keys..." },
              { value: "Real-time", label: "Monitoring", subtext: "Network agent deployment" },
              { value: "100%", label: "HIPAA Focused", subtext: "Healthcare-first design" }
            ].map((stat, i) => (
              <div key={i} className="bg-card border border-border rounded-sm p-6 card-hover">
                <div className="text-3xl md:text-4xl font-black font-[Chivo] text-primary">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                {stat.subtext && <div className="text-xs text-muted-foreground/70 mt-0.5">{stat.subtext}</div>}
              </div>
            ))}
          </motion.div>

          {/* AI Governance Flow - Expandable Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              {
                icon: Eye,
                step: "01",
                title: "Monitor",
                color: "cyan",
                description: "Track all AI tool usage across your organization in real-time",
                expandedTitle: "Complete Visibility Into AI Usage",
                expandedDetails: [
                  "See exactly which AI tools employees are using",
                  "Track usage patterns by department, team, or individual",
                  "Real-time dashboard with usage analytics",
                  "Identify shadow AI adoption before it becomes a risk"
                ],
                customerBenefit: "Know exactly what's happening with AI in your organization - no more blind spots."
              },
              {
                icon: Search,
                step: "02",
                title: "Detect",
                color: "primary",
                description: "Identify when employees send data to ChatGPT, Claude, Copilot, and 20+ AI services",
                expandedTitle: "Catch Data Exposure In Real-Time",
                expandedDetails: [
                  "Automatic detection of 20+ AI services including ChatGPT, Claude, Gemini",
                  "Monitor both web-based and API-based AI tool usage",
                  "Identify risky prompts containing sensitive information",
                  "Instant alerts when PHI/PII is about to be shared"
                ],
                customerBenefit: "Stop data leaks the moment they happen - not days or weeks later in an audit."
              },
              {
                icon: ShieldCheck,
                step: "03",
                title: "Protect",
                color: "emerald",
                description: "Scan all outbound data for SSN, PHI, PII, and credentials before it leaves",
                expandedTitle: "Intelligent Data Classification",
                expandedDetails: [
                  "Scan for SSN, credit cards, medical records, and diagnoses",
                  "Detect API keys, passwords, and credentials",
                  "Identify patient names, DOB, addresses, and contact info",
                  "HIPAA and GDPR-aware detection patterns"
                ],
                customerBenefit: "Your sensitive data is classified and protected automatically - 24/7."
              },
              {
                icon: Ban,
                step: "04",
                title: "Prevent",
                color: "red",
                description: "Auto-block requests containing critical sensitive data to non-compliant AI services",
                expandedTitle: "Automatic Breach Prevention",
                expandedDetails: [
                  "Auto-block PHI before it reaches non-HIPAA AI services",
                  "Configurable blocking rules per data type",
                  "Instant email alerts to security team",
                  "Complete audit trail for compliance documentation"
                ],
                customerBenefit: "Sleep better knowing patient data can't leak to AI - even if employees try."
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className={`bg-card border border-border rounded-sm p-6 h-full card-hover border-l-4 transition-all duration-300 ${
                  item.color === 'cyan' ? 'border-l-cyan-500 group-hover:border-cyan-500/50' :
                  item.color === 'primary' ? 'border-l-primary group-hover:border-primary/50' :
                  item.color === 'emerald' ? 'border-l-emerald-500 group-hover:border-emerald-500/50' :
                  'border-l-red-500 group-hover:border-red-500/50'
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`h-12 w-12 rounded-sm flex items-center justify-center transition-all duration-300 ${
                      item.color === 'cyan' ? 'bg-cyan-500/10 group-hover:bg-cyan-500/20' :
                      item.color === 'primary' ? 'bg-primary/10 group-hover:bg-primary/20' :
                      item.color === 'emerald' ? 'bg-emerald-500/10 group-hover:bg-emerald-500/20' :
                      'bg-red-500/10 group-hover:bg-red-500/20'
                    }`}>
                      <item.icon className={`h-6 w-6 ${
                        item.color === 'cyan' ? 'text-cyan-500' :
                        item.color === 'primary' ? 'text-primary' :
                        item.color === 'emerald' ? 'text-emerald-500' :
                        'text-red-500'
                      }`} />
                    </div>
                    <span className={`text-4xl font-black font-[Chivo] opacity-20 group-hover:opacity-40 transition-opacity ${
                      item.color === 'cyan' ? 'text-cyan-500' :
                      item.color === 'primary' ? 'text-primary' :
                      item.color === 'emerald' ? 'text-emerald-500' :
                      'text-red-500'
                    }`}>{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold font-[Chivo] mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                  
                  {/* Expanded content on hover */}
                  <div className="max-h-0 overflow-hidden group-hover:max-h-96 transition-all duration-500 ease-in-out">
                    <div className="pt-4 mt-4 border-t border-border/50">
                      <h4 className={`font-semibold mb-3 ${
                        item.color === 'cyan' ? 'text-cyan-400' :
                        item.color === 'primary' ? 'text-primary' :
                        item.color === 'emerald' ? 'text-emerald-400' :
                        'text-red-400'
                      }`}>{item.expandedTitle}</h4>
                      <ul className="space-y-2 mb-4">
                        {item.expandedDetails.map((detail, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className={`h-4 w-4 shrink-0 mt-0.5 ${
                              item.color === 'cyan' ? 'text-cyan-500' :
                              item.color === 'primary' ? 'text-primary' :
                              item.color === 'emerald' ? 'text-emerald-500' :
                              'text-red-500'
                            }`} />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                      <p className={`text-sm font-medium italic ${
                        item.color === 'cyan' ? 'text-cyan-400/80' :
                        item.color === 'primary' ? 'text-primary/80' :
                        item.color === 'emerald' ? 'text-emerald-400/80' :
                        'text-red-400/80'
                      }`}>"{item.customerBenefit}"</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Two Column: AI Tools + Sensitive Data */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* AI Tools Monitored */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-sm p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-sm bg-cyan-500/10 flex items-center justify-center">
                  <Brain className="h-6 w-6 text-cyan-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-[Chivo]">AI Services We Monitor</h3>
                  <p className="text-sm text-muted-foreground">Real-time detection of 20+ AI tools</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {aiTools.map((tool, i) => (
                  <span 
                    key={i}
                    className="px-3 py-1.5 text-sm rounded-sm bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                  >
                    {tool}
                  </span>
                ))}
                <span className="px-3 py-1.5 text-sm rounded-sm bg-muted text-muted-foreground">
                  +10 more
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Our network agent monitors all outbound traffic to known AI service domains, 
                providing complete visibility into which tools your employees are using and when.
              </p>
            </motion.div>

            {/* Sensitive Data Detection */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-sm p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-[Chivo]">PHI & PII Detection</h3>
                  <p className="text-sm text-muted-foreground">Scan for sensitive data before it leaks</p>
                </div>
              </div>
              <div className="space-y-3 mb-6">
                {sensitiveDataTypes.map((data, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-sm bg-muted/30">
                    <span className="text-sm">{data.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      data.risk === 'critical' ? 'bg-red-500/20 text-red-400' :
                      data.risk === 'high' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {data.risk.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Every request to AI services is scanned for HIPAA-regulated PHI and GDPR/CCPA-regulated PII 
                before it leaves your network.
              </p>
            </motion.div>
          </div>

          {/* HIPAA Warning */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-red-500/10 border border-red-500/30 rounded-sm p-8"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-sm bg-red-500/20 flex items-center justify-center shrink-0">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold font-[Chivo] text-red-400 mb-2">
                  Most AI Services Are NOT HIPAA Compliant
                </h3>
                <p className="text-muted-foreground mb-4">
                  ChatGPT, Claude, Gemini, and most popular AI tools do not have Business Associate Agreements (BAAs) 
                  and are not designed to handle Protected Health Information. When employees paste patient data 
                  into these tools, your organization faces potential HIPAA violations with fines up to <strong className="text-red-400">$1.5 million per incident</strong>.
                </p>
                <div className="flex items-center gap-4">
                  <Button onClick={() => navigate('/register')} className="bg-red-500 hover:bg-red-600">
                    Protect Your Organization
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* AI Governance Options */}
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-sm p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-[Chivo]">Log Analysis Mode</h3>
                  <p className="text-sm text-muted-foreground">Analyze existing proxy/firewall logs</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Upload your network logs to detect AI tool usage patterns. Identify which employees 
                are using which AI services and when sensitive data may have been exposed.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Analyze historical usage patterns",
                  "Identify high-risk AI tool adoption",
                  "Generate compliance reports",
                  "No agent installation required"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="text-sm text-muted-foreground">
                Included in <span className="text-primary font-medium">Starter</span> plan and above
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border border-cyan-500/50 rounded-sm p-8 relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 px-2 py-1 bg-cyan-500 text-white text-xs font-bold rounded-full">
                RECOMMENDED
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-sm bg-cyan-500/10 flex items-center justify-center">
                  <Terminal className="h-6 w-6 text-cyan-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-[Chivo]">Real-Time Network Agent</h3>
                  <p className="text-sm text-muted-foreground">24/7 monitoring + auto-blocking</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Deploy our lightweight agent on your network gateway. It monitors all traffic to AI services 
                in real-time and can automatically block requests containing PHI/PII.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Real-time AI usage monitoring",
                  "Auto-block PHI/PII before it leaks",
                  "Configurable alerting thresholds",
                  "Central dashboard reporting"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="text-sm text-muted-foreground">
                Included in <span className="text-cyan-400 font-medium">Professional</span> plan and above
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Bot Threat Defense Section */}
      <section id="threat-defense" className="py-24 bg-gradient-to-b from-background via-red-950/5 to-background border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-red-500 text-sm mb-6">
                <ShieldAlert className="h-4 w-4" />
                <span>Threat Protection</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-[Chivo] mb-4">
                AI Bot <span className="text-red-500">Threat Defense</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Malicious bots and AI-powered scanners probe healthcare systems 24/7. 
                We detect them in real-time and generate instant fixes to protect your infrastructure.
              </p>
            </motion.div>
          </div>

          {/* Threat Flow Diagram */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Eye,
                step: "01",
                title: "Detect",
                description: "Our AI analyzes your server logs in real-time to identify vulnerability scanners, AI crawlers, and malicious bots",
                threats: ["Nikto", "SQLmap", "Nmap", "Nuclei", "GPTBot", "AI Crawlers"]
              },
              {
                icon: Bot,
                step: "02",
                title: "Identify",
                description: "Classify threats by severity, track attacker IPs, and detect behavioral anomalies like rapid-fire requests",
                threats: ["Critical Scanners", "Injection Attempts", "Path Traversal", "Rate Abuse"]
              },
              {
                icon: Ban,
                step: "03",
                title: "Protect",
                description: "Auto-generate firewall rules and security configurations ready to deploy on your infrastructure",
                threats: ["iptables Rules", "Nginx Config", "Apache .htaccess", "AWS WAF", "Cloudflare"]
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-card border border-border rounded-sm p-8 h-full card-hover">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="h-14 w-14 rounded-sm bg-red-500/10 flex items-center justify-center">
                      <item.icon className="h-7 w-7 text-red-500" />
                    </div>
                    <span className="text-5xl font-black font-[Chivo] text-red-500/20">{item.step}</span>
                  </div>
                  <h3 className="text-2xl font-bold font-[Chivo] mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-6">{item.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.threats.map((threat, j) => (
                      <span 
                        key={j} 
                        className="px-2 py-1 text-xs rounded-sm bg-red-500/10 text-red-400 border border-red-500/20"
                      >
                        {threat}
                      </span>
                    ))}
                  </div>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-8 w-8 text-red-500/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Threat Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-red-950/20 via-red-900/10 to-red-950/20 border border-red-500/20 rounded-sm p-8"
          >
            <div className="grid md:grid-cols-4 gap-8 text-center">
              {[
                { value: "50+", label: "Bot Signatures", subtext: "Detected automatically" },
                { value: "5", label: "Fix Formats", subtext: "iptables, nginx, Apache, CF, AWS" },
                { value: "<1s", label: "Detection Time", subtext: "Real-time analysis" },
                { value: "24/7", label: "Protection", subtext: "With installed agent" }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-black font-[Chivo] text-red-500">{stat.value}</div>
                  <div className="text-sm text-foreground mt-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground">{stat.subtext}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pen Testing Section */}
      <section id="pen-testing" className="py-24 border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-[Chivo] mb-4">
              AI-Powered <span className="text-primary">Penetration Testing</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive security assessments designed for healthcare compliance. 
              Identify vulnerabilities before attackers do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {penTestFeatures.map((feature, i) => (
              <FeatureCard key={i} feature={feature} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section id="compliance" className="py-24 bg-card/50 border-y border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold font-[Chivo] mb-6">
                Multi-Framework<br />
                <span className="text-primary">Compliance Coverage</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                From HIPAA to GDPR to the new EU AI Act - we help you stay compliant 
                across all major healthcare and data privacy regulations.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {complianceFrameworks.map((framework, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-sm p-6 card-hover"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="font-bold font-[Chivo]">{framework.name}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{framework.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-[Chivo] mb-4">
              Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Choose the plan that fits your organization&apos;s security needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Starter",
                price: "$499",
                period: "/month",
                description: "For small clinics and practices",
                features: [
                  "AI Usage Log Analysis",
                  "PHI/PII Detection Scanner",
                  "5 Web Application Scans",
                  "HIPAA Compliance Checks",
                  "Basic Threat Detection",
                  "PDF Reports",
                  "Email Support"
                ]
              },
              {
                name: "Professional",
                price: "$1,499",
                period: "/month",
                description: "For growing healthcare organizations",
                features: [
                  "Real-Time AI Governance Agent",
                  "Auto-Block PHI/PII Leaks",
                  "Unlimited Web & Network Scans",
                  "All Compliance Frameworks",
                  "AI-Powered Remediation",
                  "Real-Time Threat Protection Agent",
                  "24/7 Monitoring & Alerts",
                  "PDF + DOCX Reports",
                  "Priority Support"
                ],
                popular: true
              },
              {
                name: "Enterprise",
                price: "Custom",
                period: "",
                description: "For large health systems",
                features: [
                  "Everything in Professional",
                  "Unlimited AI Governance Agents",
                  "Unlimited Protection Agents",
                  "Custom Policy Engine",
                  "SIEM Integration",
                  "Dedicated Account Manager",
                  "On-Premise Deployment",
                  "SLA Guarantee",
                  "24/7 Phone Support"
                ]
              }
            ].map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-card border rounded-sm p-8 ${
                  plan.popular ? 'border-primary glow-primary' : 'border-border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold font-[Chivo] mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-black font-[Chivo] text-primary">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-muted-foreground mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${plan.popular ? 'glow-primary' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => navigate('/register')}
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                >
                  Get Started
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 border-t border-border/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Brain className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold font-[Chivo] mb-6">
            Your Employees Are Using AI.<br />
            <span className="text-yellow-400">Are You Watching?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Healthcare data breaches cost an average of $10.9 million per incident. 
            Don&apos;t let a ChatGPT prompt become your next compliance nightmare.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="glow-primary text-lg px-8"
              onClick={() => navigate('/register')}
              data-testid="cta-start-btn"
            >
              Start Monitoring AI Usage Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/risk-assessment')}
              data-testid="cta-assessment-btn"
            >
              Take Free Risk Assessment
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* HIPAA Compliance Badge */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-6 px-6 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-500">HIPAA Compliant</span>
              </div>
              <div className="h-4 w-px bg-emerald-500/30" />
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-500">SOC 2 Ready</span>
              </div>
              <div className="h-4 w-px bg-emerald-500/30" />
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-500">256-bit Encryption</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              <span className="font-bold font-[Chivo]">AI Governance Office</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-foreground transition-colors">BAA Request</a>
              <a href="#" className="hover:text-foreground transition-colors">Contact</a>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 AI Governance Office. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
